import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import ZoneManager from './components/ZoneManager';
import Validator from './components/Validator';
import Documentation from './components/Documentation';
import AIChat from './components/AIChat';
import UserManagement from './components/UserManagement';
import ApiKeyManager from './components/ApiKeyManager';
import GlobalSearch from './components/GlobalSearch';
import AuditLog from './components/AuditLog';
import ZoneRecordsPage from './pages/ZoneRecordsPage';
import './styles/App.css';

function AppContent() {
    const location = useLocation();
    const { user, logout, isAuthenticated, isAdmin } = useAuth();
    const [zoneBreadcrumb, setZoneBreadcrumb] = useState(null);

    const getBreadcrumb = () => {
        const breadcrumbs = {
            '/': 'Dashboard',
            '/dashboard': 'Dashboard',
            '/zones': 'Gerenciar Dominios',
            '/validator': 'Validar DNS',
            '/assistant': 'Assistente IA',
            '/docs': 'Documentacao',
            '/users': 'Usuarios',
            '/api-keys': 'Chaves API',
            '/search': 'Buscar Registros',
            '/audit': 'Historico',
        };

        if (location.pathname.startsWith('/zones/')) {
            return 'Gerenciar Dominios';
        }

        return breadcrumbs[location.pathname] || 'Dashboard';
    };

    useEffect(() => {
        if (!location.pathname.startsWith('/zones/')) {
            setZoneBreadcrumb(null);
        }
    }, [location.pathname]);

    // Don't show main layout on login/callback pages
    const isAuthPage = location.pathname === '/login' || location.pathname === '/auth/callback';
    if (isAuthPage) {
        return (
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
            </Routes>
        );
    }

    return (
        <ProtectedRoute>
            <div className="app">
                <header className="header">
                    <div className="header-content">
                        <div className="header-left">
                            <h1>Cloudflare DNS Manager</h1>
                            <p className="header-subtitle">
                                Gerencie e valide seus registros DNS na Cloudflare
                            </p>
                        </div>
                        {isAuthenticated && user && (
                            <div className="header-user">
                                {user.picture && (
                                    <img
                                        src={user.picture}
                                        alt={user.name}
                                        className="header-user-avatar"
                                        referrerPolicy="no-referrer"
                                    />
                                )}
                                <span className="header-user-name">{user.name}</span>
                                {isAdmin && <span className="header-admin-badge">Admin</span>}
                                <button className="btn-logout" onClick={logout}>
                                    Sair
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <div className="container">
                    <div className="breadcrumb">
                        <span className="breadcrumb-home">Inicio</span>
                        <span className="breadcrumb-separator">&rsaquo;</span>
                        <span className="breadcrumb-current">{getBreadcrumb()}</span>
                        {zoneBreadcrumb && (
                            <>
                                <span className="breadcrumb-separator">&rsaquo;</span>
                                <span className="breadcrumb-zone">{zoneBreadcrumb}</span>
                            </>
                        )}
                    </div>

                    <div className="tabs">
                        <NavLink
                            to="/dashboard"
                            className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                        >
                            Dashboard
                        </NavLink>
                        <NavLink
                            to="/zones"
                            className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                        >
                            Gerenciar Dominios
                        </NavLink>
                        <NavLink
                            to="/validator"
                            className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                        >
                            Validar DNS
                        </NavLink>
                        <NavLink
                            to="/search"
                            className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                        >
                            Buscar Registros
                        </NavLink>
                        <NavLink
                            to="/assistant"
                            className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                        >
                            Assistente IA
                        </NavLink>
                        <NavLink
                            to="/docs"
                            className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                        >
                            Documentacao
                        </NavLink>
                        {isAdmin && (
                            <NavLink
                                to="/audit"
                                className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                            >
                                Historico
                            </NavLink>
                        )}
                        {isAdmin && (
                            <NavLink
                                to="/api-keys"
                                className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                            >
                                Chaves API
                            </NavLink>
                        )}
                        {isAdmin && (
                            <NavLink
                                to="/users"
                                className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}
                            >
                                Usuarios
                            </NavLink>
                        )}
                    </div>

                    <div className="tab-content">
                        <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/zones" element={<ZoneManager onZoneChange={setZoneBreadcrumb} />} />
                            <Route path="/zones/:zoneId" element={<ZoneRecordsPage onZoneChange={setZoneBreadcrumb} />} />
                            <Route path="/validator" element={<Validator />} />
                            <Route path="/search" element={<GlobalSearch />} />
                            <Route path="/assistant" element={<AIChat />} />
                            <Route path="/docs" element={<Documentation />} />
                            {isAdmin && <Route path="/audit" element={<AuditLog />} />}
                            {isAdmin && <Route path="/api-keys" element={<ApiKeyManager />} />}
                            {isAdmin && <Route path="/users" element={<UserManagement />} />}
                        </Routes>
                    </div>
                </div>
            </div>
        </ProtectedRoute>
    );
}

function App() {
    return (
        <Router>
            <AuthProvider>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/*" element={<AppContent />} />
                </Routes>
            </AuthProvider>
        </Router>
    );
}

export default App;
